import { AlgorithmTraceCallback, Chromosome, Particle, GenerationProgressCallback } from '../types/algorithm';
import { calculateChromosomeFitness, calculateParticleFitness } from '../utils/fitnessCalculator';
import {
  tournamentSelection,
  singlePointCrossover,
  mutate,
} from './geneticAlgorithm';
import {
  initializeSwarm,
  findGlobalBest,
  updateParticle,
} from './pso';
import { SeededRNG, createSeededRNG } from '../utils/seededRNG';

/**
 * Generalized Adaptive PSO (GAPSO) implementation from scratch
 * Hybrid of PSO and GA - uses GA operators (crossover) in PSO framework
 */

/**
 * Particle parent selection for the GA phase.
 */
function selectParticleIndex(
  swarm: Particle[],
  selectionRate: number,
  rng: SeededRNG
): number {
  if (!rng.randomChance(selectionRate / 100)) {
    return rng.randomInt(swarm.length);
  }

  // Create temporary chromosome-like structures for tournament selection
  const tempChromosomes = swarm.map(p => ({
    x: p.position,
    fitness: p.fitness,
    rwp: 0,
  }));
  return tournamentSelection(tempChromosomes, rng);
}

function tryFallbackMutation(
  child: Chromosome,
  parentFitness: number,
  childNumber: number,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  generation: number,
  mutationRate: number,
  rng: SeededRNG,
  trace?: AlgorithmTraceCallback
): Chromosome {
  calculateChromosomeFitness(child, matrix, taskCount, serviceCount);

  if (child.fitness > parentFitness || !rng.randomChance(mutationRate / 100)) {
    return child;
  }

  const mutated = mutate(child, taskCount, serviceCount, rng);
  calculateChromosomeFitness(mutated, matrix, taskCount, serviceCount);
  const accepted = mutated.fitness > child.fitness;

  trace?.({
    generation: generation + 1,
    phase: 'mutation',
    action: 'fallback_mutate_crossover_child',
    detail: `Tried random-gene mutation on non-improving GAPSO child ${childNumber}.`,
    previousFitness: child.fitness,
    fitness: mutated.fitness,
    accepted,
  });

  return accepted ? mutated : child;
}

/**
 * GAPSO evolution for one generation
 */
function evolveGAPSO(
  swarm: Particle[],
  globalBest: Particle,
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  generation: number,
  maxGenerations: number,
  selectionRate: number,
  crossoverRate: number,
  mutationRate: number,
  rng: SeededRNG,
  trace?: AlgorithmTraceCallback
): void {
  // Linearly decreasing inertia weight
  const wMax = 0.9;
  const wMin = 0.4;
  const w = wMax - ((wMax - wMin) * generation) / maxGenerations;

  const c1 = 2.0;
  const c2 = 2.0;

  // PSO update for all particles
  for (let particleIndex = 0; particleIndex < swarm.length; particleIndex++) {
    const particle = swarm[particleIndex];
    const previousFitness = particle.fitness;
    const previousPosition = [...particle.position];
    updateParticle(particle, globalBest, taskCount, serviceCount, w, c1, c2, rng);
    const changedGenes = particle.position.reduce(
      (count, value, index) => count + (value !== previousPosition[index] ? 1 : 0),
      0
    );
    trace?.({
      generation: generation + 1,
      phase: 'movement',
      action: 'update_velocity_position',
      detail: 'Updated particle velocity and discrete service position.',
      entityIndex: particleIndex,
      metadata: { changedGenes, inertiaWeight: w },
    });
    calculateParticleFitness(particle, matrix, taskCount, serviceCount);
    trace?.({
      generation: generation + 1,
      phase: 'evaluation',
      action: 'evaluate_particle',
      detail: 'Evaluated particle fitness after PSO movement.',
      entityIndex: particleIndex,
      previousFitness,
      fitness: particle.fitness,
    });
    
    // Update personal best if current fitness is better
    if (particle.fitness > particle.personalBestFitness) {
      particle.personalBestFitness = particle.fitness;
      particle.personalBest = [...particle.position];
      trace?.({
        generation: generation + 1,
        phase: 'best_update',
        action: 'update_personal_best',
        detail: 'Updated particle personal best.',
        entityIndex: particleIndex,
        fitness: particle.fitness,
        accepted: true,
        solution: particle.position,
      });
    }
  }

  // GA crossover operations (hybrid enhancement)
  // Process pairs of particles for crossover using tournament selection
  const numPairs = Math.floor(swarm.length / 2);
  
  for (let i = 0; i < numPairs; i++) {
    // Apply crossover with crossoverRate probability per mating event
    if (rng.randomChance(crossoverRate / 100)) {
      // Use the shared parent-selection policy for the GA phase.
      const parent1Idx = selectParticleIndex(swarm, selectionRate, rng);
      let parent2Idx = selectParticleIndex(swarm, selectionRate, rng);
      // Ensure different parents (with retry limit to prevent infinite loops)
      let retries = 0;
      const maxRetries = 10;
      while (parent2Idx === parent1Idx && swarm.length > 1 && retries < maxRetries) {
        parent2Idx = selectParticleIndex(swarm, selectionRate, rng);
        retries++;
      }
      // If still same after retries, use adjacent particle
      if (parent2Idx === parent1Idx && swarm.length > 1) {
        parent2Idx = (parent1Idx + 1) % swarm.length;
      }
      trace?.({
        generation: generation + 1,
        phase: 'selection',
        action: 'select_ga_phase_parents',
        detail: 'Selected particle parents for GAPSO crossover/mutation phase.',
        entityIndex: parent1Idx,
        partnerIndex: parent2Idx,
        metadata: {
          parent1Fitness: swarm[parent1Idx].fitness,
          parent2Fitness: swarm[parent2Idx].fitness,
        },
      });

      // Create temporary chromosomes for crossover
      const parent1 = { 
        x: [...swarm[parent1Idx].position], 
        fitness: swarm[parent1Idx].fitness, 
        rwp: 0 
      };
      const parent2 = { 
        x: [...swarm[parent2Idx].position], 
        fitness: swarm[parent2Idx].fitness, 
        rwp: 0 
      };

      // Get both children from crossover using seeded RNG
      let [child1, child2] = singlePointCrossover(parent1, parent2, taskCount, rng);
      trace?.({
        generation: generation + 1,
        phase: 'crossover',
        action: 'single_point_crossover',
        detail: 'Created two particle candidates using GA crossover.',
      });

      child1 = tryFallbackMutation(
        child1,
        swarm[parent1Idx].fitness,
        1,
        taskCount,
        serviceCount,
        matrix,
        generation,
        mutationRate,
        rng,
        trace
      );
      child2 = tryFallbackMutation(
        child2,
        swarm[parent2Idx].fitness,
        2,
        taskCount,
        serviceCount,
        matrix,
        generation,
        mutationRate,
        rng,
        trace
      );

      // Create new particles from children with cloned velocity arrays
      const newParticle1: Particle = {
        position: child1.x,
        velocity: [...swarm[parent1Idx].velocity], // Clone velocity array
        personalBest: [...child1.x], // Initialize with child's position
        fitness: child1.fitness,
        personalBestFitness: 0,
        rwp: 0,
      };

      const newParticle2: Particle = {
        position: child2.x,
        velocity: [...swarm[parent2Idx].velocity], // Clone velocity array
        personalBest: [...child2.x], // Initialize with child's position
        fitness: child2.fitness,
        personalBestFitness: 0,
        rwp: 0,
      };

      trace?.({
        generation: generation + 1,
        phase: 'evaluation',
        action: 'evaluate_hybrid_children',
        detail: 'Evaluated GAPSO crossover/mutation children.',
        fitness: Math.max(newParticle1.fitness, newParticle2.fitness),
        metadata: {
          child1Fitness: newParticle1.fitness,
          child2Fitness: newParticle2.fitness,
        },
      });

      // Update personal best for children: compare with parent's personal best, not just current fitness
      // Fixed: Properly initialize personal best by comparing with parent's personal best
      newParticle1.personalBestFitness = Math.max(
        newParticle1.fitness,
        swarm[parent1Idx].personalBestFitness
      );
      if (newParticle1.personalBestFitness === newParticle1.fitness) {
        newParticle1.personalBest = [...newParticle1.position];
      } else {
        newParticle1.personalBest = [...swarm[parent1Idx].personalBest];
      }

      newParticle2.personalBestFitness = Math.max(
        newParticle2.fitness,
        swarm[parent2Idx].personalBestFitness
      );
      if (newParticle2.personalBestFitness === newParticle2.fitness) {
        newParticle2.personalBest = [...newParticle2.position];
      } else {
        newParticle2.personalBest = [...swarm[parent2Idx].personalBest];
      }

      // Replacement policy: replace parents with children if children are better
      if (newParticle1.fitness > swarm[parent1Idx].fitness) {
        trace?.({
          generation: generation + 1,
          phase: 'replacement',
          action: 'replace_parent_with_child',
          detail: 'Replaced first parent particle with fitter crossover child.',
          entityIndex: parent1Idx,
          previousFitness: swarm[parent1Idx].fitness,
          fitness: newParticle1.fitness,
          accepted: true,
          solution: newParticle1.position,
        });
        swarm[parent1Idx] = newParticle1;
      }
      if (newParticle2.fitness > swarm[parent2Idx].fitness) {
        trace?.({
          generation: generation + 1,
          phase: 'replacement',
          action: 'replace_parent_with_child',
          detail: 'Replaced second parent particle with fitter crossover child.',
          entityIndex: parent2Idx,
          previousFitness: swarm[parent2Idx].fitness,
          fitness: newParticle2.fitness,
          accepted: true,
          solution: newParticle2.position,
        });
        swarm[parent2Idx] = newParticle2;
      }
    }
  }
}

/**
 * Runs GAPSO for specified generations
 */
export async function runGAPSO(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    selectionRate: number;
    crossoverRate: number;
    mutationRate: number;
  },
  onGenerationComplete?: GenerationProgressCallback,
  seed?: number,
  runtimeOptions?: {
    yieldForProgress?: boolean;
    reportEveryGeneration?: boolean;
    trace?: AlgorithmTraceCallback;
  }
): Promise<Particle> {
  // Create seeded RNG for reproducibility
  const rng = createSeededRNG(seed);
  const shouldYieldForProgress = runtimeOptions?.yieldForProgress ?? true;
  const shouldReportEveryGeneration = runtimeOptions?.reportEveryGeneration ?? false;
  const trace = runtimeOptions?.trace;
  
  // Initialize swarm
  let swarm = initializeSwarm(parameters.populationSize, taskCount, serviceCount, rng);
  trace?.({
    generation: 0,
    phase: 'initialization',
    action: 'initialize_swarm',
    detail: `Created ${parameters.populationSize} particles.`,
    metadata: { populationSize: parameters.populationSize, taskCount, serviceCount },
  });

  // Evaluate initial swarm
  for (const particle of swarm) {
    calculateParticleFitness(particle, matrix, taskCount, serviceCount);
    particle.personalBestFitness = particle.fitness;
    particle.personalBest = [...particle.position];
  }

  let globalBest = findGlobalBest(swarm);
  trace?.({
    generation: 0,
    phase: 'evaluation',
    action: 'evaluate_initial_swarm',
    detail: 'Evaluated all particles and initialized personal/global bests.',
    bestFitness: globalBest.fitness,
    solution: globalBest.position,
  });

  // Evolution loop
  let lastUpdateTime = Date.now();
  const UPDATE_INTERVAL = 100; // Update UI every 100ms (PERFORMANCE OPTIMIZATION)
  
  for (let gen = 0; gen < parameters.generations; gen++) {
    evolveGAPSO(
      swarm,
      globalBest,
      taskCount,
      serviceCount,
      matrix,
      gen,
      parameters.generations,
      parameters.selectionRate,
      parameters.crossoverRate,
      parameters.mutationRate,
      rng,
      trace
    );
    const candidateGlobalBest = findGlobalBest(swarm);
    if (candidateGlobalBest.fitness > globalBest.fitness) {
      globalBest = candidateGlobalBest;
      trace?.({
        generation: gen + 1,
        phase: 'best_update',
        action: 'update_global_best',
        detail: 'Updated global best particle.',
        bestFitness: globalBest.fitness,
        accepted: true,
        solution: globalBest.position,
      });
    }

    // Batch UI updates - only update every 100ms or every 10 generations (PERFORMANCE OPTIMIZATION)
    const now = Date.now();
    const shouldUpdate =
      shouldReportEveryGeneration ||
      (now - lastUpdateTime > UPDATE_INTERVAL) ||
      (gen % 10 === 9) ||
      (gen === parameters.generations - 1);
    
    if (shouldUpdate) {
      if (onGenerationComplete) {
        onGenerationComplete(gen + 1, globalBest.fitness, globalBest.position);
      }
      trace?.({
        generation: gen + 1,
        phase: 'generation',
        action: 'generation_complete',
        detail: 'Generation best particle recorded.',
        bestFitness: globalBest.fitness,
        solution: globalBest.position,
      });
      lastUpdateTime = now;
      if (shouldYieldForProgress) {
        // Yield control to allow UI updates when running interactively.
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
  }

  return globalBest;
}
